<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <template v-else>
      <permission-group-list
        :groups="permissionGroups"
        :subject-labels="subjectLabels"
        @create="createDialog = true"
        @edit="openEditDialog"
        @delete="handleDelete"
      />

      <v-card
        outlined
        class="mt-6"
      >
        <v-card-title>customロール ユーザー一覧</v-card-title>

        <v-data-table
          :items="customUsers"
          :headers="userHeaders"
          :header-props="{ sortByText: 'ソート' }"
          :footer-props="{ itemsPerPageText: '行/ページ:' }"
          no-data-text="customロールのユーザーがいません"
        >
          <template #[`item.permissionGroups`]="{ item }">
            <v-chip
              v-for="group in item.permissionGroups"
              :key="group.id"
              small
              class="ma-1"
            >
              {{ group.name }}
            </v-chip>
            <span v-if="!item.permissionGroups || item.permissionGroups.length === 0">
              未割り当て
            </span>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-btn
              small
              color="primary"
              @click="openAssignDialog(item)"
            >
              グループ割り当て
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <create-group-dialog
        v-model="createDialog"
        :permissions="permissions"
        :subject-labels="subjectLabels"
        @create="handleCreate"
      />

      <assign-group-dialog
        v-model="assignDialog"
        :user="targetUser"
        :groups="permissionGroups"
        :assigned-group-ids="targetUserGroupIds"
        @assign="handleAssign"
      />

      <edit-group-dialog
        v-model="editDialog"
        :group="targetGroup"
        :permissions="permissions"
        :subject-labels="subjectLabels"
        @add="handleAddPermission"
        @remove="handleRemovePermission"
        @update-name="handleUpdateName"
      />
    </template>
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import Vue from 'vue'
import useCompInit from '@/views/composable/useCompInit'
import PermissionGroupApi from '@/api/v2/admin/permissionGroups'
import PermissionGroupList from './components/PermissionGroupList.vue'
import CreateGroupDialog from './components/CreateGroupDialog.vue'
import AssignGroupDialog from './components/AssignGroupDialog.vue'
import EditGroupDialog from './components/EditGroupDialog.vue'

export default {
  components: {
    PermissionGroupList,
    CreateGroupDialog,
    AssignGroupDialog,
    EditGroupDialog,
  },
  setup() {
    const { isLoading, initWith } = useCompInit()
    const permissionGroups = ref([])
    const permissions = ref([])
    const subjectLabels = ref({})
    const customUsersData = ref([])
    const createDialog = ref(false)
    const assignDialog = ref(false)
    const editDialog = ref(false)
    const targetUser = ref(null)
    const targetGroup = ref(null)

    const customUsers = computed(() => {
      if (!customUsersData.value) return []

      return customUsersData.value.map(user => ({
        id: user.id,
        name: user.name,
        email: user.email,
        permissionGroups: user.permission_groups || [],
      }))
    })

    const targetUserGroupIds = computed(() => {
      if (!targetUser.value) return []

      return (targetUser.value.permissionGroups || []).map(g => g.id)
    })

    const userHeaders = [
      { text: '名前', value: 'name' },
      { text: 'メール', value: 'email' },
      { text: '割り当て済みグループ', value: 'permissionGroups', sortable: false },
      {
        text: '操作', value: 'actions', sortable: false, width: '160px',
      },
    ]

    const fetchData = async () => {
      const [groupsRes, permissionsRes, customUsersRes] = await Promise.all([
        PermissionGroupApi.getPermissionGroups(),
        PermissionGroupApi.getPermissions({ category: 'waiter' }),
        PermissionGroupApi.getCustomUsers(),
      ])

      if (groupsRes?.data?.data) {
        permissionGroups.value = groupsRes.data.data.permission_groups || []
      }

      if (permissionsRes?.data?.data) {
        permissions.value = permissionsRes.data.data.permissions || []
        subjectLabels.value = permissionsRes.data.data.subject_labels || {}
      }

      if (customUsersRes?.data?.data) {
        customUsersData.value = customUsersRes.data.data.custom_users || []
      }
    }

    const handleCreate = async ({ name, permissionIds }) => {
      try {
        await PermissionGroupApi.createPermissionGroup({
          permissionGroup: {
            name,
            permissionGroupAssignmentsAttributes: permissionIds.map(id => ({
              permissionId: id,
            })),
          },
        })
        Vue.prototype.$toast.success('パーミッショングループを作成しました')
        await fetchData()
      } catch (e) {
        Vue.prototype.$toast.error('パーミッショングループの作成に失敗しました')
      }
    }

    const handleDelete = async group => {
      try {
        await PermissionGroupApi.deletePermissionGroup(group.id)
        Vue.prototype.$toast.success('パーミッショングループを削除しました')
        await fetchData()
      } catch (e) {
        Vue.prototype.$toast.error('パーミッショングループの削除に失敗しました')
      }
    }

    const openAssignDialog = user => {
      targetUser.value = user
      assignDialog.value = true
    }

    const openEditDialog = group => {
      targetGroup.value = group
      editDialog.value = true
    }

    const handleAssign = async ({ userId, permissionGroupIds }) => {
      try {
        await PermissionGroupApi.assignPermissionGroups(userId, permissionGroupIds)
        Vue.prototype.$toast.success('グループを割り当てました')
        await fetchData()
      } catch (e) {
        Vue.prototype.$toast.error('グループの割り当てに失敗しました')
      }
    }

    const handleAddPermission = async ({ groupId, permissionId }) => {
      try {
        const res = await PermissionGroupApi.updatePermissionGroup(groupId, {
          addPermissionId: permissionId,
        })
        if (res?.data?.data?.permission_group) {
          const updatedGroup = res.data.data.permission_group
          const idx = permissionGroups.value.findIndex(g => g.id === groupId)
          if (idx !== -1) {
            permissionGroups.value[idx].permissions = updatedGroup.permissions
          }
          if (targetGroup.value && targetGroup.value.id === groupId) {
            targetGroup.value.permissions = updatedGroup.permissions
          }
        }
        Vue.prototype.$toast.success('権限を追加しました')
      } catch (e) {
        Vue.prototype.$toast.error('権限の追加に失敗しました')
      }
    }

    const handleUpdateName = async ({ groupId, name }) => {
      try {
        const res = await PermissionGroupApi.updatePermissionGroup(groupId, { name })
        if (res?.data?.data?.permission_group) {
          const updatedGroup = res.data.data.permission_group
          const idx = permissionGroups.value.findIndex(g => g.id === groupId)
          if (idx !== -1) {
            permissionGroups.value[idx].name = updatedGroup.name
          }
          if (targetGroup.value && targetGroup.value.id === groupId) {
            targetGroup.value.name = updatedGroup.name
          }
        }
        Vue.prototype.$toast.success('グループ名を変更しました')
      } catch (e) {
        Vue.prototype.$toast.error('グループ名の変更に失敗しました')
      }
    }

    const handleRemovePermission = async ({ groupId, permissionId }) => {
      try {
        const res = await PermissionGroupApi.updatePermissionGroup(groupId, {
          removePermissionId: permissionId,
        })
        if (res?.data?.data?.permission_group) {
          const updatedGroup = res.data.data.permission_group
          const idx = permissionGroups.value.findIndex(g => g.id === groupId)
          if (idx !== -1) {
            permissionGroups.value[idx].permissions = updatedGroup.permissions
          }
          if (targetGroup.value && targetGroup.value.id === groupId) {
            targetGroup.value.permissions = updatedGroup.permissions
          }
        }
        Vue.prototype.$toast.success('権限を削除しました')
      } catch (e) {
        Vue.prototype.$toast.error('権限の削除に失敗しました')
      }
    }

    initWith([fetchData()])

    return {
      isLoading,
      permissionGroups,
      permissions,
      subjectLabels,
      customUsers,
      userHeaders,
      createDialog,
      assignDialog,
      editDialog,
      targetUser,
      targetGroup,
      targetUserGroupIds,
      handleCreate,
      handleDelete,
      openAssignDialog,
      openEditDialog,
      handleAssign,
      handleAddPermission,
      handleRemovePermission,
      handleUpdateName,
    }
  },
}
</script>
